<script lang="ts">
    import { cn } from '$lib/utils/cn';

    import { German } from 'flatpickr/dist/l10n/de';
    import { english as English } from 'flatpickr/dist/l10n/default';
    import uniqueId from 'lodash/uniqueId';
    import { afterUpdate } from 'svelte';
    import DeviceDetector from 'svelte-device-detector';
    import clickOutside from 'svelte-outside-click';
    import { animateScroll } from 'svelte-scrollto-element';

    import DatePickerInputActionButtons from './DatePickerInputActionButtons.svelte';
    import DatePickerInputContent from './DatePickerInputContent.svelte';
    import BottomSheet from '../../../_wrappers/BottomSheet.svelte';
    import Dropdown from '../../../_wrappers/Dropdown.svelte';
    import Emoji from '../../../_wrappers/Emoji.svelte';
    import { resolveInputStyling } from '../../_utils/resolveInputStyling';
    import { isDayDisabled, isDayFocusable } from '../../helper';
    import StatusIcon from '../StatusIcon.svelte';

    import type { Language } from '$lib/types/Language';
    import type { Theme } from '$lib/types/Theme';

    import 'flatpickr/dist/flatpickr.css';

    export let placeholder = '';
    export let customOptions = {
        pastDatesDisabled: false,
        futureDatesDisabled: false,
        weekendsDisabled: false,
    };
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let finalFormInput: {
        name: string;
        onChange: (value: string) => void;
        onBlur?: () => void;
        onFocus?: () => void;
    } = {
        name: '',
        onChange: () => {},
        onBlur: () => {},
        onFocus: () => {},
    };
    export let errorMessage = '';
    export let focusColor = '';
    export let outlineColor = '';
    export let theme: Theme | undefined = undefined;
    export let language: Language = 'en';
    // Property introduced for Storybook to set a fixed date for visual tests
    export let defaultDate: Date | undefined = undefined;
    // Property introduced for Storybook to control the initial open state of the calendar
    export let initialOpen = false;
    // Property introduced for Storybook to mock the current date
    export let mockNowDate: string | undefined = undefined;

    let open = initialOpen;
    let focused = false;

    let showError = invalid && touched;

    let inputWrapperClasses;
    let inputClasses;
    let chevronSvgClasses;

    let dateSelected: Date | undefined;
    let lastConfirmedDate: Date | undefined;
    let formattedDate: string | undefined;
    let inputRef: HTMLInputElement | undefined;
    let confirmBtnRef: HTMLButtonElement | undefined;
    let clearBtnRef: HTMLButtonElement | undefined;

    const nextMonthArrowId = uniqueId('next-month-arrow');
    const prevMonthArrowId = uniqueId('prev-month-arrow');
    const dropdownId = uniqueId('dropdown');

    const translations = {
        de: {
            clear: 'Löschen',
            confirm: 'Speichern',
            l10n: German,
        },
        en: {
            clear: 'Clear',
            confirm: 'Confirm',
            l10n: {
                ...English,
                weekdays: {
                    ...English.weekdays,
                    shorthand: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                },
            },
        },
    };

    const now = mockNowDate
        ? new Date(mockNowDate).setHours(0, 0, 0, 0)
        : new Date().setHours(0, 0, 0, 0);

    afterUpdate(() => {
        showError = !lastConfirmedDate && required && touched;
    });

    const setConfirmButtonRef = (ref: HTMLButtonElement) => {
        confirmBtnRef = ref;
    };

    const setClearButtonRef = (ref: HTMLButtonElement) => {
        clearBtnRef = ref;
    };

    const handleInputClick = () => {
        if (open) {
            handleCalendarClose();
        } else {
            handleCalendarOpen();
        }
    };

    const handleClickOutside = () => {
        if (!open) return;

        handleCalendarClose();
    };

    const handleGlobalKeyDown = (event: KeyboardEvent) => {
        const travelBack = event.shiftKey && event.key === 'Tab';

        if (travelBack && open) {
            handleCalendarClose();
        }

        // Handle [space] or [enter] keydown
        if (event.key === ' ' || event.key === 'Enter') {
            event.preventDefault();

            if (open) {
                handleCalendarClose();
            } else {
                handleCalendarOpen();
            }
        }
    };

    const handleGlobalBlur = () => {
        if (!open) {
            focused = false;
        } else {
            return;
        }

        finalFormInput.onBlur?.();
    };

    // Handle Shift + Tab from Calendar Days
    const focusBackFromCalendar = () => {
        const nextMonthArrow = document.getElementById(nextMonthArrowId);
        const prevMonthArrow = document.getElementById(prevMonthArrowId);

        if (
            nextMonthArrow &&
            nextMonthArrow.parentElement &&
            nextMonthArrow.parentElement.className.indexOf('flatpickr-disabled') < 0
        ) {
            nextMonthArrow.focus();
        } else if (
            prevMonthArrow &&
            prevMonthArrow.parentElement &&
            prevMonthArrow.parentElement.className.indexOf('flatpickr-disabled') < 0
        ) {
            prevMonthArrow.focus();
        } else {
            inputRef?.focus();
        }
    };

    const handleDayKeyDown = (event: KeyboardEvent & { target: any }) => {
        const travelBack = event.shiftKey && event.key === 'Tab';
        const travelForward = event.key === 'Tab';
        const enterPressed = event.key === 'Enter';
        const spacePressed = event.key === ' ';

        if (travelBack) {
            if (!isDayDisabled(event.target.previousElementSibling)) {
                event.target.previousElementSibling?.focus();
            } else {
                focusBackFromCalendar();
            }
        } else if (travelForward) {
            if (!isDayFocusable(event.target.nextElementSibling)) {
                event.preventDefault();

                if (dateSelected) {
                    clearBtnRef?.focus();
                }
            }
        }

        // Handle select date
        if (enterPressed || spacePressed) {
            event.preventDefault();
            dateSelected = event.target.dateObj;
            setTimeout(() => {
                confirmBtnRef?.focus();
            });
        }
    };

    const handleEscClick = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            handleCalendarClose();
        }
    };

    const handleFormattedDate = (dateSelected: Date | undefined) => {
        formattedDate = dateSelected && new Date(dateSelected).toLocaleDateString();
    };

    const handleClear = (e: Event) => {
        e.preventDefault();

        dateSelected = undefined;
        lastConfirmedDate = undefined;
        formattedDate = undefined;

        // Clear global input
        if (inputRef) {
            inputRef.value = '';
        }

        finalFormInput.onChange('');
        handleCalendarClose();
    };

    const handleConfirm = (e: Event) => {
        e.preventDefault();

        if (dateSelected) {
            const dateSelectedUTCTimestamp = Date.UTC(
                dateSelected.getFullYear(),
                dateSelected.getMonth(),
                dateSelected.getDate(),
            );
            const dateSelectedUTC = new Date(dateSelectedUTCTimestamp);
            const dateSelectedIso = dateSelectedUTC.toISOString();

            lastConfirmedDate = dateSelected;

            // Update global input
            if (inputRef) {
                inputRef.value = dateSelectedIso;
            }
            finalFormInput.onChange(dateSelectedIso);
            handleFormattedDate(dateSelected);
            handleCalendarClose();
        }
    };

    const handleChange = (value: any) => {
        dateSelected = value?.detail?.[0]?.[0];
    };

    const handleCalendarOpen = () => {
        open = true;
        focused = true;
        scrollIntoView();
        inputRef?.focus();
    };

    const handleCalendarClose = () => {
        // Default to last confirmed date
        dateSelected = lastConfirmedDate;
        handleFormattedDate(lastConfirmedDate);

        open = false;
        focused = false;
        touched = true;
        inputRef?.focus();
    };

    const scrollIntoView = () => {
        const dropdown = document.querySelector(`#${dropdownId}`);

        if (dropdown) {
            // Offset the height of the input field
            animateScroll?.scrollTo({ element: dropdown as HTMLElement, offset: -64 });
        }
    };

    const options = {
        enableTime: false,
        inline: true,
        static: true,
        locale: {
            ...translations[language].l10n,
            firstDayOfWeek: language === 'en' ? 0 : 1,
        },
        monthSelectorType: 'static',
        closeOnSelect: false,
        defaultDate: defaultDate, // Set initial calendar view to this date (for Storybook)
        minDate: customOptions.pastDatesDisabled ? now : undefined,
        maxDate: customOptions.futureDatesDisabled ? now : undefined,
        nextArrow: `<div id="${nextMonthArrowId}" class="month-picker-arrow" tabindex="0">
                        <svg class="fun-rotate-90 fun-ml-0.5" width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M6.55806 0.433058C6.80214 0.188981 7.19786 0.188981 7.44194 0.433058L13.6919 6.68306C13.936 6.92714 13.936 7.32286 13.6919 7.56694C13.4479 7.81102 13.0521 7.81102 12.8081 7.56694L7 1.75888L1.19194 7.56694C0.947864 7.81102 0.552136 7.81102 0.308058 7.56694C0.0639806 7.32286 0.0639806 6.92714 0.308058 6.68306L6.55806 0.433058Z"
                                fill="#6B7280"
                            />
                        </svg>
                    </div>`,
        prevArrow: `<div id="${prevMonthArrowId}" class="month-picker-arrow" tabindex="0">
                        <svg class='fun--rotate-90 fun-mr-0.5' width="14" height="8" viewBox="0 0 14 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M6.55806 0.433058C6.80214 0.188981 7.19786 0.188981 7.44194 0.433058L13.6919 6.68306C13.936 6.92714 13.936 7.32286 13.6919 7.56694C13.4479 7.81102 13.0521 7.81102 12.8081 7.56694L7 1.75888L1.19194 7.56694C0.947864 7.81102 0.552136 7.81102 0.308058 7.56694C0.0639806 7.32286 0.0639806 6.92714 0.308058 6.68306L6.55806 0.433058Z"
                                fill="#6B7280"
                            />
                        </svg>
                    </div>`,
        onDayCreate: (_a: any, _b: any, _c: any, data: any) => {
            if (!isDayDisabled(data)) {
                // Make days focusable + keyboard controls + add 0's
                data.tabIndex = 0;
                data.onkeydown = handleDayKeyDown;
                data.innerHTML =
                    "<div class='flatpickr-day-box'>" +
                    (+data.innerHTML < 10 ? '0' : '') +
                    data.innerHTML +
                    '</div>';
            } else if (data.className.indexOf('flatpickr-disabled') > -1) {
                data.innerHTML = (+data.innerHTML < 10 ? '0' : '') + data.innerHTML;
                data.tabIndex = '-1';
            }
        },
        disable: [
            (date: Date) => {
                // Disable weekends if needed
                if (customOptions.weekendsDisabled) {
                    return date.getDay() === 0 || date.getDay() === 6;
                }
            },
        ],
    };

    const {
        borderRadiusClass,
        shadowClass,
        borderColor,
        backgroundColor,
        textColor,
        mutedTextColor,
    } = resolveInputStyling({
        theme,
        shadowComponent: 'textInput',
    });

    $: inputWrapperClasses = cn(
        'fun-relative fun-box-border fun-flex fun-transition-all',
        borderRadiusClass,
        shadowClass,
        {
            ['fun-h-20']: showError,
        },
    );

    $: inputClasses = cn(
        'fun-box-border fun-flex fun-w-full fun-cursor-pointer fun-flex-row fun-border fun-bg-white fun-pl-4 fun-pr-10 fun-font-ui fun-text-base fun-font-light fun-caret-transparent fun-outline-none fun-ring-2 fun-ring-transparent fun-transition-all placeholder:fun-truncate disabled:fun-opacity-100',
        !mutedTextColor && 'fun-text-black fun-placeholder-gray-500',
        borderRadiusClass,
        {
            ['fun-border-[var(--focusColor)] fun-ring-[var(--outlineColor)]']: focused,
            ['fun-pr-32']: !required && !dateSelected,
            ['fun-h-20 fun-pb-[26px] fun-pr-24']: showError,
            ['fun-h-14']: !showError,
            ['fun-pl-12']: showEmoji,
            ['fun-pr-24']: !required && pristine,
        },
    );

    $: chevronSvgClasses = cn(
        'fun-pointer-events-none fun-absolute fun-top-5 fun-size-4 fun-transform fun-cursor-pointer fun-text-gray-500 fun-transition-all fun-duration-[400ms] fun-ease-out',
        {
            'fun--rotate-180': !open,
            'fun-rotate-0': open,
            'fun-right-16': showError,
            'fun-right-6': !showError,
        },
    );
</script>

<div class="fun-relative" use:clickOutside={handleClickOutside} on:keydown={handleEscClick}>
    <div class={inputWrapperClasses}>
        {#if showEmoji}
            <u
                class="fun-absolute fun-left-4 fun-mt-5 fun-flex fun-h-[18px] fun-select-none fun-items-center fun-no-underline"
                aria-hidden="true"
            >
                <Emoji {emoji} size="20px" />
            </u>
        {/if}

        {#if !required && !invalid && pristine}
            <div
                class="fun-pointer-events-none fun-absolute fun-right-14 fun-flex fun-h-full fun-items-center fun-text-xs fun-font-light fun-text-gray-500 fun-no-underline"
            >
                <span>(optional)</span>
            </div>
        {/if}

        {#if showError}
            <StatusIcon showCheckmark={false} showSuccess={false} {showError} />
        {/if}

        <!-- Click catcher -->
        <button
            class="fun-absolute fun-inset-0 fun-z-10"
            type="button"
            on:click={handleInputClick}
            aria-label={placeholder}
        />

        <input
            readonly
            class="fun-absolute fun--z-10 fun-opacity-0"
            name={finalFormInput.name}
            bind:value={formattedDate}
            on:focus={() => {
                focused = true;
                finalFormInput.onFocus?.();
            }}
            on:blur={handleGlobalBlur}
            on:keydown={handleGlobalKeyDown}
            bind:this={inputRef}
            aria-label={placeholder}
        />

        <input
            disabled
            class={inputClasses}
            class:themed-placeholder={!!mutedTextColor}
            style:background-color={backgroundColor}
            style:border-color={!focused ? borderColor : undefined}
            style:color={textColor}
            style:--placeholder-color={mutedTextColor}
            {placeholder}
            tabindex="-1"
            autocomplete="off"
            bind:value={formattedDate}
            style="--outlineColor: {outlineColor}; --focusColor: {focusColor}"
        />

        <svg
            class={chevronSvgClasses}
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6.55806 0.433058C6.80214 0.188981 7.19786 0.188981 7.44194 0.433058L13.6919 6.68306C13.936 6.92714 13.936 7.32286 13.6919 7.56694C13.4479 7.81102 13.0521 7.81102 12.8081 7.56694L7 1.75888L1.19194 7.56694C0.947864 7.81102 0.552136 7.81102 0.308058 7.56694C0.0639806 7.32286 0.0639806 6.92714 0.308058 6.68306L6.55806 0.433058Z"
                fill="#6B7280"
            />
        </svg>

        {#if showError}
            <div
                class={cn(
                    'fun-absolute fun-left-4 fun-top-11 fun-text-xs fun-font-normal fun-leading-tight fun-text-red-600',
                    {
                        'fun-left-14': showEmoji,
                    },
                )}
            >
                {errorMessage}
            </div>
        {/if}
    </div>

    <DeviceDetector showInDevice={['mobile']}>
        <BottomSheet
            {open}
            title={placeholder}
            handleClose={handleCalendarClose}
            actionButtonsComponent={DatePickerInputActionButtons}
            actionButtonsProps={{
                disabled: !dateSelected,
                setConfirmButtonRef,
                setClearButtonRef,
                btnClearLabel: translations[language].clear,
                btnConfirmLabel: translations[language].confirm,
                handleConfirm,
                handleClear,
                open,
            }}
            {language}
        >
            <DatePickerInputContent
                {options}
                {dateSelected}
                {handleChange}
                {prevMonthArrowId}
                {nextMonthArrowId}
                {open}
            />
        </BottomSheet>
    </DeviceDetector>

    <DeviceDetector showInDevice={['desktop', 'tablet']}>
        <!-- This wrapper class will allow closing the dropdown by clicking in the empty area besides the calendar. -->
        <Dropdown id={dropdownId} {open} wrapperClass="right-auto" contentClass="fun-max-h-[29rem]">
            <DatePickerInputContent
                {options}
                {dateSelected}
                {handleChange}
                {prevMonthArrowId}
                {nextMonthArrowId}
                {open}
            />
            <DatePickerInputActionButtons
                disabled={!dateSelected}
                {setConfirmButtonRef}
                {setClearButtonRef}
                btnClearLabel={translations[language].clear}
                btnConfirmLabel={translations[language].confirm}
                {handleConfirm}
                {handleClear}
                {open}
            />
        </Dropdown>
    </DeviceDetector>
</div>

<style lang="scss">
    @import './styles/flatpickr.styles.scss';

    input.themed-placeholder::placeholder {
        color: var(--placeholder-color);
    }
</style>
